import * as React from 'react';
import { ContentFilterRssController, ContentFilterRssControllerProps } from './ContentFilterRss.controller';
import { gql, useApolloClient } from '@apollo/client';
import {
  ExternalRssPost,
  ThirdPartyPost,
  ThirdPartyPostAttachmentType,
  ThirdPartyPostMediaType,
  useSearchRssPostsLazyQuery
} from '../../../../models/graphql';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../providers/AppProvider';
import { FindContentContext } from '../FindContentProvider';
import { UseFormReturn } from 'react-hook-form';
import { ScrollContext } from '../../../../providers/ScrollProvider';
import { getUrlParams } from '../../../../helpers/getUrlParams';

export type ContentFilterRssApolloProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
};

gql`
  query SearchRssPosts($workspaceId: ID!, $url: String!, $term: String, $cursor: String) {
    searchRssPosts(workspaceId: $workspaceId, url: $url, term: $term, cursor: $cursor) {
      cursor
      items {
        ...ExternalRssPostFields
      }
    }
  }
`;

export type fetchRssParameters = {
  url: string;
  term: string;
};

export const ContentFilterRssApollo = (props: ContentFilterRssApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [searchRssPosts, rssPostResults] = useSearchRssPostsLazyQuery();
  const [searchParameters, setSearchParameters] = useState<fetchRssParameters | undefined>(undefined);
  const [reachedLimit, setReachedLimit] = useState<boolean>(false);
  const { selectedWorkspaceId } = useContext(AppContext);
  const { setLoading, setLoadingMore, setPosts, setSelectedPosts, hasSearched, setHasSearched } =
    useContext(FindContentContext);

  const { data: posts, fetchMore } = rssPostResults;
  const { setOnScrollBottom } = useContext(ScrollContext);

  const fetchRssPosts = useCallback(
    async (parameters: fetchRssParameters) => {
      if (selectedWorkspaceId) {
        setLoading(true);

        await searchRssPosts({
          variables: { workspaceId: selectedWorkspaceId, ...parameters }
        })
          .then(results => {
            if (results.error) {
              throw results.error;
            }

            setHasSearched(true);
          })
          .finally(() => setLoading(false));
      }
    },
    [searchRssPosts, selectedWorkspaceId, setHasSearched, setLoading]
  );

  // Mapping to allow for RSS posts to be the same as ThirdPartyPost so Post/Grid can work the same
  const mapExternalPostToThirdPartyPost = useCallback((post: ExternalRssPost, index: number) => {
    const media = post.imageUrl ? [{ type: ThirdPartyPostMediaType.PHOTO, url: post.imageUrl }] : [];

    const url = getUrlParams(String(post.postUrl).trim());
    const domain = url?.hostname.replace(/^www\./, '');

    const mappedPost: ThirdPartyPost = {
      media,
      platform: '',
      postUrl: post.postUrl ?? '',
      text: post.content,
      textMentions: [],
      attachmentTypes: [ThirdPartyPostAttachmentType.LINK],
      createdAt: post.createdAt,
      externalId: `rss-${index}`,
      externalProfile: {
        externalId: `rss-${index}`,
        displayName: `${domain}`,
        username: '',
        profileUrl: post.postUrl,
        platform: 'RSS'
      },
      linkUrl: post.postUrl,
      linkImageUrl: post.imageUrl
    };

    return mappedPost;
  }, []);

  const purgeRssCache = useCallback(() => {
    cache.evict({ fieldName: 'searchRssPosts' });

    setPosts([]);
    setSelectedPosts([]);
    setReachedLimit(false);
  }, [cache, setPosts, setSelectedPosts]);

  useEffect(() => {
    if (posts?.searchRssPosts.items) {
      setPosts(posts.searchRssPosts.items.map((p, index) => mapExternalPostToThirdPartyPost(p, index)));
    }
  }, [mapExternalPostToThirdPartyPost, posts, setPosts]);

  useEffect(() => {
    setOnScrollBottom(() => {
      // Don't proceed if we already reached limit
      if (!reachedLimit && hasSearched) {
        setLoadingMore(true);

        const cursor = posts?.searchRssPosts.cursor;
        fetchMore({ variables: { ...searchParameters, cursor } })
          .then(results => {
            // If no results, we should not fetch more
            setReachedLimit(results.data.searchRssPosts.items.length === 0);

            setPosts(prev => [
              ...prev,
              ...results.data.searchRssPosts.items.map((p, index) => mapExternalPostToThirdPartyPost(p, index))
            ]);
          })
          .finally(() => {
            setLoadingMore(false);
          });
      }
    });
  }, [
    fetchMore,
    hasSearched,
    mapExternalPostToThirdPartyPost,
    posts,
    reachedLimit,
    searchParameters,
    setLoadingMore,
    setOnScrollBottom,
    setPosts
  ]);

  const controllerProps: ContentFilterRssControllerProps = {
    ...props,
    searchParameters,
    setSearchParameters,
    fetchRssPosts,
    purgeRssCache
  };

  return <ContentFilterRssController {...controllerProps} />;
};
